// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import {
	Container,
	Header,
	Form,
	FormField,
	Input,
	Button,
	SpaceBetween,
	Flashbar,
	Box,
	Spinner,
} from "@cloudscape-design/components";
import { FlashbarProps } from "@cloudscape-design/components/flashbar";

import {
	getCurrentCostRates,
	updateCostRates,
	validateCostRate,
} from "../../util/costRateConfig";

export default function CostRateSettings() {
	const { t } = useTranslation();

	const [characterCostRate, setCharacterCostRate] = useState("");
	const [characterCostRateError, setCharacterCostRateError] = useState("");
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [flashItems, setFlashItems] = useState<FlashbarProps.MessageDefinition[]>([]);
	const [currentCharacterRate, setCurrentCharacterRate] = useState<number | null>(null);

	useEffect(() => {
		async function loadRates() {
			try {
				const rates = await getCurrentCostRates();
				setCurrentCharacterRate(rates.characterCostRate);
				setCharacterCostRate(String(rates.characterCostRate));
			} catch {
				setFlashItems([
					{
						type: "error",
						content: t("cost_rate_load_error"),
						dismissible: true,
						onDismiss: () => setFlashItems([]),
					},
				]);
			} finally {
				setLoading(false);
			}
		}
		loadRates();
	}, [t]);

	function validateField(value: string): string {
		if (value.trim() === "") {
			return t("cost_rate_validation_required");
		}
		const num = Number(value);
		if (!validateCostRate(num)) {
			return t("cost_rate_validation_invalid");
		}
		return "";
	}

	function handleSubmit() {
		const charError = validateField(characterCostRate);
		setCharacterCostRateError(charError);

		if (charError) {
			return;
		}

		setSubmitting(true);
		setFlashItems([]);

		updateCostRates({
			characterCostRate: Number(characterCostRate),
		})
			.then((updated) => {
				setCurrentCharacterRate(updated.characterCostRate);
				setFlashItems([
					{
						type: "success",
						content: t("cost_rate_update_success"),
						dismissible: true,
						onDismiss: () => setFlashItems([]),
					},
				]);
			})
			.catch(() => {
				setFlashItems([
					{
						type: "error",
						content: t("cost_rate_update_error"),
						dismissible: true,
						onDismiss: () => setFlashItems([]),
					},
				]);
			})
			.finally(() => {
				setSubmitting(false);
			});
	}

	if (loading) {
		return (
			<Box textAlign="center" margin={{ vertical: "l" }}>
				<Spinner size="large" />
			</Box>
		);
	}

	return (
		<Container
			header={
				<Header
					variant="h2"
					description={t("cost_rate_settings_description")}
				>
					{t("cost_rate_settings_title")}
				</Header>
			}
		>
			<SpaceBetween size="l">
				<Flashbar items={flashItems} />

				{currentCharacterRate !== null && (
					<SpaceBetween size="xs">
						<Box variant="awsui-key-label">
							{t("cost_rate_current_character_rate")}
						</Box>
						<Box>{currentCharacterRate}</Box>
					</SpaceBetween>
				)}

				<Form
					actions={
						<Button
							variant="primary"
							onClick={handleSubmit}
							loading={submitting}
						>
							{t("cost_rate_submit")}
						</Button>
					}
				>
					<SpaceBetween size="m">
						<FormField
							label={t("cost_rate_character_label")}
							errorText={characterCostRateError}
						>
							<Input
								value={characterCostRate}
								onChange={({ detail }) => {
									setCharacterCostRate(detail.value);
									setCharacterCostRateError("");
								}}
								type="number"
								inputMode="decimal"
								data-testid="character-cost-rate-input"
							/>
						</FormField>
					</SpaceBetween>
				</Form>
			</SpaceBetween>
		</Container>
	);
}
